import {isMobileDevice, getGlobal, isNodeJS} from "../../compat";
import {createWorker} from "../DtWorkerCreator";
import {copySanitizedLoadContext} from "../../net/endpoints";

import {PriorityQueue} from "./PriorityQueue";

import {av, avp} from "@adsk/lmv";

export var MESH_RECEIVE_EVENT = "meshReceived";
export var MESH_FAILED_EVENT = "meshFailed";
export var MATERIAL_RECEIVE_EVENT = "materialReceived";
export var MATERIAL_FAILED_EVENT = "materialFailed";

const MAX_REQUESTS_PER_WORKER = 10000;
const INITIAL_REQUESTS_PER_WORKER = 200;

let useOpfs;

function initLoadContextGeomCache(msg, loadContext) {

	if (useOpfs === undefined) {
		// ENABLE_MODEL_CACHE is set via the viewer settings panel.
		useOpfs = !isNodeJS() && !!getGlobal().ENABLE_MODEL_CACHE;
	}
	copySanitizedLoadContext(msg, loadContext);
	msg.useOpfs = useOpfs;
	return msg;
}

// Helper function used for cache cleanup
function compareGeomsByImportance(geom1, geom2) {
	return geom1.importance - geom2.importance;
}

/** Shared cache of BufferGeometries and material JSONs used by different OtgLoaders. */
export function DtResourceCache(sessionId) {

	// all geometries, indexed by geom hashes
	var _geoms = this._geoms = new Map();
	var _mats = new Map();

	// A single geometry may be requested by one or more model loaders.
	// This map keeps track of requests already in progress so that
	// we don't issue multiple simultaneously
	var _hash2Requests = new Map();

	var _workTotal = 0;
	var _workDone = 0;
	var _currentProgress = -1;
	// Store the first timestamp at creation time to measure load times from viewer initialization across all models.
	var _timings = [performance.now()];

	// worker for geometry loading
	var NUM_WORKERS = 1;//isMobileDevice() ? 2 : 6;
	// This value changes with the number of enqueued requests.
	// We start with a low value to properly prioritize more queued up requests first.
	var _requestsPerWorker = INITIAL_REQUESTS_PER_WORKER;
	var _workers = [];

	for (var i = 0; i < NUM_WORKERS; i++) {
		const worker = createWorker('Geometry Worker', sessionId);
		worker.addEventListener('message', handleMessage);
		_workers.push(worker);
	}

	this.initialized = true;

	// track memory consumption
	this.byteSize = 0;
	this.refCount = 0;

	// A request is called in-progress if we have sent it to the worker and didn't receive a result yet.
	// We restrict the number of _requestsInProgress. If the limit is reached, all additional requests
	// are enqueued in _waitingRequests.
	var _requestsInProgress = 0;
	var _totalRequested = 0;
	var _totalReceived = 0;
	var _lifeTimeTotal = 0;


	var _timeout = undefined;

	var _queue = new PriorityQueue((a, b) => a.importance > b.importance);

	var _this = this;

	// Keep track of unused geometries.
	// A geometry is tracked as unused if it was used by at least one model and then got removed from all models.
	// This map keep track of which geometries are unused (it stores geom.id -> geometry instance).
	var _unusedGeomsMap = new Map();

	// mem limits for cache cleanup
	var MB = 1024 * 1024;
	var _maxMemory = 100 * MB; // geometry limit at which cleanup is activated
	var _minCleanup = 50 * MB; // minimum amount of freed memory for a single cleanup run

	// Needed for cache-cleanup to check which RenderModels are loaded
	var _viewers = [];

	this.addViewer = function (viewer) {
		_viewers.push(viewer);
	};

	this.removeViewer = function (viewer) {
		const index = _viewers.indexOf(viewer);

		if (index !== -1) {
			_viewers.splice(index, 1);
		}

		if (_viewers.length === 0) {
			this.dtor();
		}
	};

	this.dtor = function () {
		_viewers = [];

		for (var i = 0; i < NUM_WORKERS; i++) {
			_workers[i].removeEventListener('message', handleMessage);
			_workers[i].terminate();
		}

		this.cleanup(true);

		this.initialized = false;
	};

	this.isIdle = function () {
		//The logic here is that the cache can be idle only after it has loaded something.
		//It will not be idle initially, when nothing has yet been loaded or queued for loading.
		//This ensures that code that waits for geometry loading to be "done" does not activate too early.
		//Note that this is consistent with calls to onGeomLoadComplete() which trigger only after _something_
		//is loaded.
		return _workTotal === 0 && _queue.isEmpty() && _lifeTimeTotal > 0;
	}

	this.addWorkInProgress = function(count) {
		if (_workTotal === 0) {
			_timings.push(performance.now());
		}

		_workTotal += count;
		for (let i = 0; i < _viewers.length; i++) {
			_viewers[i].impl.trackFrameBudget(false);
		}
	}

	this.updateProgress = function(count) {
		_workDone += count;

		//Signal loading progress to the attached viewers
		const loadProgressPercent = _workDone / _workTotal * 100;
		const loadProgressRounded = Math.round(loadProgressPercent);
		if (_currentProgress !== loadProgressRounded) {
			_currentProgress = loadProgressRounded;
			for (let i = 0; i < _viewers.length; i++) {
				_viewers[i].impl.signalProgress(_currentProgress, av.ProgressState.LOADING, null);
			}
		}

		//Reset progress percentage when we reach an idle state
		if (loadProgressPercent >= 100 && _workTotal > 0 && _workDone === _workTotal) {
			const t1 = performance.now();
			console.log(`Loading took ${t1 - _timings[_timings.length - 1]} (${t1 - _timings[0]})`);

			// Reset stats
			_lifeTimeTotal += _workTotal;
			_workTotal = 0;
			_workDone = 0;
			_currentProgress = -1;
			_requestsPerWorker = INITIAL_REQUESTS_PER_WORKER;

			// Tell the viewer to repaint once we reach idle state (presumably we are done loading something)
			for (let i = 0; i < _viewers.length; i++) {
				_viewers[i].impl.onGeomLoadComplete();
				_viewers[i].impl.trackFrameBudget(true);
			}
		}
	}

	function scheduleQueueProcessing() {
		if (!_timeout && !_queue.isEmpty()) {
			_timeout = setTimeout(() => {
				_timeout = null;
				processQueuedItems();
			}, 0);
		}
	}

	// function to handle messages from OtgLoadWorker (posted in onGeometryLoaded)
	function handleMessage(msg) {

		if (!msg.data) {
			return;
		}

		if (msg.data.error) {
			var error = msg.data.error;

			// get hash for which request failed
			var hash = error.args ? error.args.hash : undefined;

			var type = error.args ? error.args.type : "g";

			// inform affected clients.
			if (hash) {
				if (type === "m") {
					_mats.set(hash, error); //create an error entry in the cache
					_this.fireEvent({type: MATERIAL_FAILED_EVENT, error: error});
					console.warn("Error loading material", hash);
				} else {
					_geoms.set(hash, error); //create an error entry in the cache
					_this.fireEvent({type: MESH_FAILED_EVENT, error: error});
					console.warn("Error loading mesh", hash);
				}

				const task = _hash2Requests.get(error.hash);
				task.callbacks.forEach(cb => cb(false));

				_hash2Requests.delete(error.hash);

				// track number of requests in progress
				_requestsInProgress--;
				_totalReceived++;

				//Schedule another spin through the task queue
				scheduleQueueProcessing();
			}
		} else if (msg.data.material) {
			var mdata = msg.data;
			// add material to cache
			var hash = mdata.hash;
			var mat = mdata.material;
			_mats.set(hash, mat);

			// pass geometry to all receiver callbacks
			_this.fireEvent({type: MATERIAL_RECEIVE_EVENT, material: mat, hash: hash});

			const task = _hash2Requests.get(mdata.hash);
			task.callbacks.forEach(cb => cb(true));

			_hash2Requests.delete(mdata.hash);

			_requestsInProgress--;
			_totalReceived++;

			//Schedule another spin through the task queue
			scheduleQueueProcessing();

		} else {

			var meshlist = msg.data;

			// Request new geometries before processing the ones we just received
			_requestsInProgress -= meshlist.length;
			_totalReceived += meshlist.length;

			if (!_queue.isEmpty()) {
				if (_timeout) {
					clearTimeout(_timeout);
					_timeout = null;
				}
				processQueuedItems();
			}

			for (var i = 0; i < meshlist.length; i++) {

				var mdata = meshlist[i];

				if (mdata.hash && mdata.mesh) {
					// convert geometry data to GeometryBuffer
					var geom = avp.meshToGeometry(mdata);

					// add geom to cache
					var hash = mdata.hash;
					_this.addGeometry(hash, geom);

					// free old unused geoms if necessary
					_this.cleanup();

					// pass geometry to all receiver callbacks
					_this.fireEvent({type: MESH_RECEIVE_EVENT, geom: geom});

					// recall and store the load order importance, so we can
					// later use it for unloading
					const task = _hash2Requests.get(mdata.hash);
					geom.importance = task.importance;

					task.callbacks.forEach(cb => cb(true));

					_hash2Requests.delete(mdata.hash);
				}
			}
		}
	}

	this.addGeometry = function(hash, geom) {
		_geoms.set(hash, geom);

		// track summed cache size in bytes
		_this.byteSize += geom.byteSize;

		if (Object.prototype.hasOwnProperty.call(geom, '_modelRefCount')) {
			return;
		}
		geom._modelRefCount = 0;

		const prototype = Object.getPrototypeOf(geom);
		if (Object.prototype.hasOwnProperty.call(prototype, 'modelRefCount')) {
			return;
		}
		Object.defineProperty(prototype, 'modelRefCount', {
			get: geometry_modelRefCount_get
		});
		prototype.setModelRefCount = geometry_setModelRefCount;
	};

	this.setGeomRefCount = function(geom, value) {
		if (geom._modelRefCount === 0 && value >= 1) {
			const unused = _unusedGeomsMap.get(geom.id);
			if (unused) {
				_unusedGeomsMap.delete(geom.id);
			}
		} else if (value === 0) {
			_unusedGeomsMap.set(geom.id, geom);
		}
		geom._modelRefCount = value;
	};

	function assignWorkerForTask(resId) {
		if (typeof resId === "number") {
			return resId % NUM_WORKERS;
		}

		return 0 | (Math.random() * NUM_WORKERS);
	}

	this.initWorker = function (modelId, loadContext) {

		// Prepare load workers
		for (var i = 0; i < NUM_WORKERS; i++) {

			var msg = {
				operation: "INIT_WORKER_OTG",
				modelId
			};

			_workers[i].doOperation(initLoadContextGeomCache(msg, loadContext));
		}
	};

	this.unregisterLoader = function(modelId, loadContext) {
		// Tell the workers that a loader has gone idle
		for (var i = 0; i < NUM_WORKERS; i++) {
			var msg = {
				operation: "UNREGISTER_LOADER_OTG",
				modelId
			};

			_workers[i].doOperation(initLoadContextGeomCache(msg, loadContext));
		}
	}

	this.updateMruTimestamps = function (loadContext) {

		//Tell each worker which ranges of the geometry pack it's responsible for.
		for (var i = 0; i < NUM_WORKERS; i++) {

			var msg = {
				operation: "UPDATE_MRU_TIMESTAMPS_OTG",
				endSession: _queue.isEmpty()
			};

			_workers[i].doOperation(initLoadContextGeomCache(msg, loadContext));
		}

	};

	this.getGeometry = function(geomHash) {
		return _geoms.get(geomHash);
	};

	/**  Get a geometry from cache or load it.
	 *    @param {string}   url         - base request url of the geometry/ies resource -- geometry hash is added to the end of that when HTTP is used to load data
	 *    @param {string}   geomHash    - hash key to identify requested geometry/ies
	 *    @param {int} geomIdx          - the geometry ID/index in the model's geometry hash list (optional, pass 0 to skip use of geometry packs)
	 *    @param {string}   queryParams - additional param passed to file query
	 *    @param {Number} importance    - importance metric for the request geometry (e.g. surface area of the object bbox)
	 */
	this.requestGeometry = function (url, geomHash, geomIdx, queryParams, importance, loadContext) {

		// if this geometry is in memory, just return it directly
		var geom = _geoms.get(geomHash);
		if (geom && geom.args) {
			//it failed to load previously
			if (isNodeJS()) {
				setTimeout(() => this.fireEvent({
					type: MESH_FAILED_EVENT,
					error: geom,
					hash: geomHash,
					repeated: true
				}), 0);
			} else {
				this.fireEvent({type: MESH_FAILED_EVENT, error: geom, hash: geomHash, repeated: true});
			}
			return;
		} else if (geom) {
			//it was already cached
			if (isNodeJS()) {
				setTimeout(() => this.fireEvent({type: MESH_RECEIVE_EVENT, geom: geom}), 0);
			} else {
				this.fireEvent({type: MESH_RECEIVE_EVENT, geom: geom});
			}
			return;
		}

		// if geometry is already loading, just increment
		// the request counter.
		var task = _hash2Requests.get(geomHash);
		if (task && task.refcount) {
			task.refcount++;
			return;
		}

		// geom is neither in memory nor loading.
		// we have to request it.
		var msg = {
			loadContext: loadContext,
			operation: "LOAD_CDN_RESOURCE_OTG",
			type: "g",
			url: url,
			hash: geomHash,
			queryParams: queryParams,
			importance: importance,
			geomIdx: geomIdx,
			refcount: 1,
			requested: false,
			callbacks: []
		};

		_totalRequested++;

		_queue.push(msg);
		_hash2Requests.set(geomHash, msg);

		// Update max number of in-flight requests, based on queue length
		this._updateMaxRequests();

		scheduleQueueProcessing();
	};

	this.requestMaterial = function (url, matHash, matIdx, queryParams, loadContext) {

		// if this material is in memory, just return it directly
		var mat = _mats.get(matHash);
		if (mat && mat.args) {
			//it failed to load previously
			setTimeout(() => this.fireEvent({
				type: MATERIAL_FAILED_EVENT,
				error: mat,
				hash: matHash,
				repeated: true
			}), 0);
			return;
		} else if (mat) {
			//it was already cached
			setTimeout(() => this.fireEvent({type: MATERIAL_RECEIVE_EVENT, material: mat, hash: matHash}), 0);
			return;
		}

		// if material is already loading, just increment
		// the request counter.
		var task = _hash2Requests.get(matHash);
		if (task && task.refcount) {
			task.refcount++;
			return;
		}

		// material is neither in memory nor loading.
		// we have to request it.
		var msg = {
			operation: "LOAD_CDN_RESOURCE_OTG",
			type: "m",
			urls: [url],
			hashes: [matHash],
			queryParams: queryParams,
			refcount: 1,
			importances: [0x7fffffff], //use highest priority for materials
			requested: true,
			callbacks: []
		};

		_hash2Requests.set(matHash, msg);

		//Material requests are sent to the worker immediately, without going through the
		//priority queue.
		var whichWorker = assignWorkerForTask(matIdx);
		_workers[whichWorker].doOperation(initLoadContextGeomCache(msg, loadContext));
		_requestsInProgress++;
		_totalRequested++;
	};

	/**
	 * Requests a batch of geometries and/or materials by their hashes.
	 * All resources must have been requested via requestGeometry / requestMaterial before, but must not have been
	 * loaded yet (i.e. must still be pending). It is the caller's responsibility to only provide hashes that meet
	 * these conditions.
	 * @param {Set<string>} hashes Set of resource hashes to request.
	 * @param {number} priority Priority with which to process the response.
	 * @returns {Promise<boolean>} Promise resolving to true if all loaded successfully, false otherwise.
	 */
	this.batchRequest = function(hashes, priority) {
		// TODO: We don't use the same implementation as LMV here.
		// Instead of actually batch-requesting all hashes that haven't been send to the worker before,
		// we simply keep processing the queue for simplicity (for the PoC, should fix it later).
		// So all we do here is wait for the given hashes to get loaded and then resolve the promise.

		let numMissingHashes = hashes.size;
		let loadCompleted;
		let promise = new Promise((resolve) => {
			loadCompleted = resolve;
		});
		let allLoadedSuccessfully = true;

		const callback = (success) => {
			--numMissingHashes;
			if (!success) {
				// A required geom or material failed to load.
				allLoadedSuccessfully = false;
			}
			if (numMissingHashes === 0) {
				loadCompleted(allLoadedSuccessfully);
			}
		};

		for (const hash of hashes) {
			// All hashes have previously been requested via loadGeometry / -material, so there are pending requests
			// for them.
			let request = _hash2Requests.get(hash);

			// requestMaterial uses setTimeout to fire the received event if the material is already there.
			// So it can happen that materials get requested by the loader that are actually already known to the OtgResourceCache.
			// So we need to catch that here.
			if (_mats.has(hash)) {
				// Because of the setTimeout, we must delay the callback.
				// Otherwise, if we fire the callback here immediately, consolidation will be attempted before the
				// loader received the material.
				setTimeout(() => callback(true), 0);
				continue;
			}

			request.callbacks.push(callback);

			if (request.requested) {
				continue;
			}

			// TODO: Don't flag as requested here yet, because we don't actually request in this method for now.
			// request.requested = true;

			// TODO: Also skip updating task priority here, because we're currently not reprioritizing while processing
			// the queue anyway.
			// request.priority = Math.max(priority, request.priority ?? 0);

			// TODO: Not sending requests from this method yet, so skipping this as well (also needs to be introduced
			// in general).
			// this._pendingRequests.push(request);
		}

		// TODO: Simply returning here to skip all of the below code for now.
		return promise;

		// If we accumulated enough work (completely arbitrary number),
		// or no more work will come this tick (because the max number of in flight requests has been reached),
		// send it immediately to keep the worker busy.
		if (this._pendingRequests.length > 1000 || _requestsInProgress + this._pendingRequests.length >= this._getMaxRequestsInFlight()) {
			this.sendRequests(this._pendingRequests);
			this._pendingRequests = [];
		} else if (!_timeout && this._pendingRequests.length > 0) {
			// Otherwise, delay sending the requests to batch with other nodes.
			// In particular when loading small nodes from cache, we don't want to send 100s of messages per tick.
			// This will be outside of the OOCTM time budget, but should be fast for < 1000 requests.
			_timeout = setTimeout(() => {
				_timeout = null;
				this.sendRequests(this._pendingRequests);
				this._pendingRequests = [];
			}, 0);
		}

		return promise;
	};

	this.hasBandwidthBudget = function() {
		return getBandwidthBudget() > 0;
	}

	function processQueuedItems() {
		// Geometry loading works by pushing requests into the queue as fragments get loaded.
		// So we start out by loading from the queue, and then cut over to the OOCTM when the BVH got ready.
		// The OOCTM prioritizes load requests per node and calls batchRequest for the hashes that haven't been loaded
		// yet. At this point, we want to skip the queue as long as the OOCTM still has download tasks.
		// Ideally we'd clear the queue entirely as soon as the OOCTM sends work, but if a very small
		// model finished, a large model might still not have a BVH and want to process the queue.
		// TODO: We don't do it this way yet, but keep using the queue for all requests. Skipping the skip for now.
		// if (OutOfCoreTileManager.hadDownloadTasksLastTick()) {
		// 	return;
		// }

		var howManyCanWeDo = getBandwidthBudget();

		// TODO: May want to avoid very small requests (see LMV).

		if (howManyCanWeDo <= 0) {
			return;
		}

		var msgPerWorker = [];
		var tasksAdded = 0;

		while (!_queue.isEmpty() && tasksAdded < howManyCanWeDo) {

			var task = _queue.pop();

			if (task.requested) {
				continue;
			}

			// Can happen when cutting over to loading from BVH. Then we still need to process the queue to clear it.
			// Note [PF]: I don't think this affects Tandem right now.
			if (_geoms.has(task.hash)) {
				continue;
			}

			task.requested = true;

			//Find which worker thread is preferred for the task
			var whichWorker = assignWorkerForTask(task.geomIdx);
			var msg = msgPerWorker[whichWorker];

			if (!msg) {
				msg = {
					operation: "LOAD_CDN_RESOURCE_OTG",
					type: "g",
					urls: [task.url],
					hashes: [task.hash],
					importances: [task.importance],
					queryParams: task.queryParams
				};

				initLoadContextGeomCache(msg, task.loadContext);
				msgPerWorker[whichWorker] = msg;
			} else {
				msg.urls.push(task.url);
				msg.hashes.push(task.hash);
				msg.importances.push(task.importance);
			}

			tasksAdded++;
		}

		for (var i = 0; i < msgPerWorker.length; i++) {
			var msg = msgPerWorker[i];
			if (msg) {
				// send request to worker
				_workers[i].doOperation(msg);
				_requestsInProgress += msg.urls.length;
			}
		}
	}

	// remove all open requests of this client
	// input is a map whose keys are geometry hashes
	this.cancelRequests = function (geomHashMap) {

		for (var [hash] of geomHashMap) {
			var task = _hash2Requests.get(hash);

			if (task)
				task.refcount--;
			/*
			if (task.refcount === 1) {
				_hash2Requests.delete(hash);
			}*/
		}

		var hiPrioList = [];
		var heap = _queue._heap;
		for (var i = 0; i < heap.length; i++) {
			var t = heap[i];
			var req = _hash2Requests.get(t.hash);
			if (req) {
				if (req.refcount)
					hiPrioList.push(t);
				else
					_hash2Requests.delete(t.hash);
			}
		}

		//TODO: perhaps we can leave requests with refcount = 0 in the queue
		//but sort the queue based on refcount so that those get deprioritized

		_totalRequested -= heap.length - hiPrioList.length;

		//Reset the priority queue with the remaining items
		_queue._heap.length = 0;
		for (let i=0; i<hiPrioList.length; i++) {
			_queue.push(hiPrioList[i]);
		}

		// TODO: To make switches faster, we should also inform the worker thread,
		//       so that it doesn't spend too much time with loading geometries that noone is waiting for.

		if (hiPrioList.length === 0) {
			//Make sure geom load event gets fired if the queue is all of a sudden empty
			for (let i = 0; i < _viewers.length; i++) {
				_viewers[i].impl.onGeomLoadComplete();
			}
		}
	};

	this.cleanup = function (force) {

		if (_unusedGeomsMap.size === 0 || (!force && this.byteSize < _maxMemory)) {
			return;
		}

		var _unusedGeoms = [];
		for (let geom of _unusedGeomsMap.values()) {
			_unusedGeoms.push(geom);
		}

		// Sort unused geoms by ascending importance
		_unusedGeoms.sort(compareGeomsByImportance);

		// Since cleanup is too expensive to run per geometry,
		// we always remove a bit more than strictly necessary,
		// so that we can load some more new geometries before we have to
		// run cleanup again.
		var targetMem = force ? 0 : _maxMemory - _minCleanup;

		// Remove geoms until we reach mem target
		var i = 0;
		for (; i < _unusedGeoms.length && this.byteSize >= targetMem; i++) {

			var geom = _unusedGeoms[i];

			// remove it from cache
			_geoms.delete(geom.hash);
			_unusedGeomsMap.delete(geom.id);

			// update mem consumption. Note that we run this only for geoms that
			// are not referenced by any RenderModel in memory, so that removing them
			// should actually free memory.
			this.byteSize -= geom.byteSize;

			// Dispose GPU mem.
			// NOTE: In case we get performance issues in Chrome, try commenting this out
			// (see hack in GeometryList.dispose)
			geom.dispose();
		}
	};

	this.getGeometry = function (hash) {
		return _geoms.get(hash);
	};

	this._updateMaxRequests = function() {
		// A very simple heuristic to increase the amount of in-flight requests depending on the queue length.
		// We start with a small value (see INITIAL_REQUESTS_PER_WORKER) to enable meaningful prioritization and then
		// increase it depending on the queue length, until the maximum value is reached.
		// Note that this method does never decrease the current value. Resetting to the default is handled separately
		// (currently once the queue is empty).
		_requestsPerWorker = Math.min(Math.max(Math.round(_queue.size() * 0.1), _requestsPerWorker), MAX_REQUESTS_PER_WORKER);
	}

	function getBandwidthBudget() {
		return _requestsPerWorker * NUM_WORKERS - _requestsInProgress;
	}
}

av.EventDispatcher.prototype.apply(DtResourceCache.prototype);

// These two functions are intentionally defined outside of the
// upper scope to make sure they don't hold a reference to the
// closure. They will be added to a prototype later and thus
// would prevent the closure from being garbage collected.
function geometry_modelRefCount_get() {
  return this._modelRefCount;
}

let warningAboutRefcountPrinted = false;
function geometry_setModelRefCount(value, geomCache) {
    if (!geomCache) {
        // This should never happen, but as a fallback we will set the refcounts to 1
        // if we don't have a geomCache to keep track of them.
        if (!warningAboutRefcountPrinted) {
            console.warn("geometry_setModelRefCount: geomCache is not defined");
            warningAboutRefcountPrinted = true;
        }
        this._modelRefCount = 1;
    } else {
        geomCache.setGeomRefCount(this, value);
    }
}
